import { Helmet } from "react-helmet";
import React, { useEffect, useMemo, useState } from "react";
import "../../styles/drivers/drivers.scss";
import { Alert, Button, Form, FormControl, Modal, Spinner, Table } from "react-bootstrap";
import axios from "axios";
import queryString from "query-string";
import Layout from "../../Layout/layout";
import { apiHost, encodeQueryData } from "../../api/apiClient";
import RoleAccessBadge from "../common/roleAccessBadge";

const blankDriver = {
  customerId: "",
  fullName: "",
  dateOfBirth: "",
  addressLine1: "",
  addressLine2: "",
  suburb: "",
  state: "VIC",
  postcode: "",
  mobile: "",
  email: "",
  emergencyName: "",
  emergencyPhone: "",
  emergencyRelation: "",
  licenceNumber: "",
  licenceClass: "C",
  licenceExpiry: "",
  endorsements: "",
  isSample: false,
};

const states = ["VIC", "NSW", "QLD", "SA", "WA", "TAS", "ACT", "NT"];
const licenceClasses = ["C", "LR", "MR", "HR", "HC", "MC"];
const sampleFirstNames = [
  "John", "Emily", "Jacob", "Olivia", "Daniel", "Sophie", "Michael", "Sarah", "Jason", "Chloe",
  "Matthew", "Jessica", "Ryan", "Lauren", "Andrew", "Emma", "David", "Amy", "Luke", "Natalie",
  "Chris", "Hannah", "Ben", "Megan", "Tom", "Isabella", "Adam", "Grace", "Nick", "Rebecca",
  "James", "Kate", "Aaron", "Melissa", "Paul", "Zoe", "Josh", "Claire", "Sam", "Tara"
];
const sampleLastNames = [
  "Smith", "Brown", "Jones", "Wilson", "Taylor", "Peters", "Roberts", "Hughes", "Dawson", "Spencer",
  "Williams", "Thompson", "White", "Martin", "Anderson", "Clark", "Lewis", "Walker", "Hall", "Young",
  "King", "Wright", "Lopez", "Nguyen", "Patel", "Kumar", "Singh", "Ali", "Hassan", "Georgiou"
];
const sampleStreets = [
  "High St", "Main Rd", "Station St", "Church St", "Victoria Rd", "Bridge Rd", "Plenty Rd", "Lygon St",
  "Elizabeth St", "Collins St", "George St", "Princes Hwy", "Nepean Hwy", "Sydney Rd", "Canterbury Rd"
];
const sampleSuburbs = [
  { name: "Richmond", state: "VIC", range: [3000, 3999] },
  { name: "Doncaster", state: "VIC", range: [3000, 3999] },
  { name: "Parramatta", state: "NSW", range: [2000, 2599] },
  { name: "Chatswood", state: "NSW", range: [2000, 2599] },
  { name: "South Brisbane", state: "QLD", range: [4000, 4999] },
  { name: "Glenelg", state: "SA", range: [5000, 5799] },
  { name: "Fremantle", state: "WA", range: [6000, 6797] },
  { name: "Hobart", state: "TAS", range: [7000, 7799] },
  { name: "Darwin City", state: "NT", range: [800, 899] },
  { name: "Belconnen", state: "ACT", range: [2600, 2618] },
];
const sampleEndorsements = ["Forklift", "DG", "Passenger", "Towing", "White Card", "None"];

const getAuthHeaders = () => ({
  Authorization: "Bearer " + localStorage.getItem("token"),
});

const normalizeRole = (role) =>
  (role || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-AU");
};

const isExpired = (value) => {
  if (!value) {
    return false;
  }

  const date = new Date(`${value}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return !Number.isNaN(date.getTime()) && date < today;
};

const toApiPayload = (driver) => ({
  customerId: driver.customerId || "",
  fullName: driver.fullName || "",
  dateOfBirth: driver.dateOfBirth || "",
  addressLine1: driver.addressLine1 || "",
  addressLine2: driver.addressLine2 || "",
  suburb: driver.suburb || "",
  state: driver.state || "",
  postcode: driver.postcode || "",
  mobile: driver.mobile || "",
  email: driver.email || "",
  emergencyName: driver.emergencyName || "",
  emergencyPhone: driver.emergencyPhone || "",
  emergencyRelation: driver.emergencyRelation || "",
  licenceNumber: driver.licenceNumber || "",
  licenceClass: driver.licenceClass || "",
  licenceExpiry: driver.licenceExpiry || "",
  endorsements: driver.endorsements || "",
  isSample: Boolean(driver.isSample),
});

const buildFormState = (driver) => ({
  ...blankDriver,
  ...driver,
});

const getResponseRows = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (!data || typeof data !== "object") {
    return [];
  }

  if (Array.isArray(data.rows)) {
    return data.rows;
  }

  if (Array.isArray(data.items)) {
    return data.items;
  }

  if (Array.isArray(data.data)) {
    return data.data;
  }

  if (Array.isArray(data.drivers)) {
    return data.drivers;
  }

  if (Array.isArray(data.$values)) {
    return data.$values;
  }

  return [];
};

const samplePick = (items) => items[Math.floor(Math.random() * items.length)];

const randomDate = (minYearsFromNow, maxYearsFromNow) => {
  const years = Math.floor(Math.random() * (maxYearsFromNow - minYearsFromNow + 1)) + minYearsFromNow;
  const date = new Date();
  date.setFullYear(date.getFullYear() + years);
  date.setMonth(Math.floor(Math.random() * 12));
  date.setDate(Math.floor(Math.random() * 28) + 1);
  return date.toISOString().split("T")[0];
};

const randomLicence = () => Array.from({ length: 8 }, () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  return chars[Math.floor(Math.random() * chars.length)];
}).join("");

const buildSampleDriver = (customerId) => {
  const first = samplePick(sampleFirstNames);
  const last = samplePick(sampleLastNames);
  const suburb = samplePick(sampleSuburbs);
  const postcode = Math.floor(Math.random() * (suburb.range[1] - suburb.range[0] + 1)) + suburb.range[0];

  return {
    ...blankDriver,
    customerId: customerId || "",
    fullName: `(SAMPLE) ${first} ${last}`,
    dateOfBirth: randomDate(-55, -24),
    addressLine1: `${Math.floor(Math.random() * 300) + 1} ${samplePick(sampleStreets)}`,
    suburb: suburb.name,
    state: suburb.state,
    postcode: String(postcode),
    mobile: `04${Math.floor(10000000 + Math.random() * 89999999)}`,
    email: `${first}.${last}`.toLowerCase() + "@example.com",
    emergencyName: `${samplePick(sampleFirstNames)} ${samplePick(sampleLastNames)}`,
    emergencyPhone: `04${Math.floor(10000000 + Math.random() * 89999999)}`,
    emergencyRelation: samplePick(["Friend", "Partner", "Sibling", "Parent"]),
    licenceNumber: randomLicence(),
    licenceClass: samplePick(licenceClasses),
    licenceExpiry: randomDate(1, 5),
    endorsements: samplePick(sampleEndorsements),
    isSample: true,
  };
};

function Drivers(props) {
  const role = normalizeRole(localStorage.getItem("role"));
  const isSystemAdmin = role === "systemadmin";
  const canManageDrivers = isSystemAdmin || role === "vtdemo" || role === "communitymanager";
  const query = queryString.parse(props.location.search);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(query.customerId || "");
  const [selectedCustomerName, setSelectedCustomerName] = useState(query.customerName || "");
  const [searchText, setSearchText] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState(blankDriver);
  const [errors, setErrors] = useState([]);
  const [notice, setNotice] = useState("");
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pinDriver, setPinDriver] = useState(null);
  const [pinSaving, setPinSaving] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteDriverTarget, setDeleteDriverTarget] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  const filteredCustomerName = useMemo(() => {
    if (selectedCustomerName) {
      return selectedCustomerName;
    }
    const customer = customers.find((item) => String(item.id) === String(selectedCustomerId));
    return customer ? customer.name : "";
  }, [customers, selectedCustomerId, selectedCustomerName]);

  const updateCustomerFilter = (customerId, customerName = "") => {
    const nextCustomerId = customerId || "";
    const matchedCustomer = customers.find((item) => String(item.id) === String(nextCustomerId));
    const nextCustomerName = customerName || (matchedCustomer ? matchedCustomer.name : "");
    const nextQuery = queryString.parse(props.location.search);

    if (nextCustomerId) {
      nextQuery.customerId = nextCustomerId;
      nextQuery.customerName = nextCustomerName;
      localStorage.setItem("customer", JSON.stringify({
        customerId: nextCustomerId,
        customerName: nextCustomerName,
      }));
    } else {
      delete nextQuery.customerId;
      delete nextQuery.customerName;
      localStorage.removeItem("customer");
    }

    const nextSearch = queryString.stringify(nextQuery);
    props.history.replace(`${props.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`);
    setPageNumber(1);
    setSelectedCustomerId(nextCustomerId);
    setSelectedCustomerName(nextCustomerName);
  };

  useEffect(() => {
    if (!canManageDrivers) {
      props.history.push("/unauthorise");
      return;
    }

    if (isSystemAdmin) {
      loadCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const locationQuery = queryString.parse(props.location.search);
    const locationCustomerId = locationQuery.customerId || "";
    const locationCustomerName = locationQuery.customerName || "";

    setPageNumber(1);
    setSelectedCustomerId(locationCustomerId);
    setSelectedCustomerName(locationCustomerName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location.search]);

  useEffect(() => {
    if (canManageDrivers) {
      loadDrivers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, searchText, pageNumber, pageSize]);

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${apiHost}/api/Customer/GetAllCustomers?AllRecords=false&FetchAll=true`, {
        headers: getAuthHeaders(),
      });
      setCustomers(getResponseRows(response.data).map((item) => ({
        id: item.id || item.Id,
        name: item.name || item.Name,
      })));
    } catch (e) {
      setNotice("Unable to load customers.");
    }
  };

  const loadDrivers = async () => {
    setLoading(true);
    setNotice("");
    try {
      const qs = encodeQueryData({
        DriverList: true,
        CustomerId: selectedCustomerId,
        SearchString: searchText,
        PageNumber: pageNumber,
        PageSize: pageSize,
      });
      const response = await axios.get(`${apiHost}/api/Customer/GetAllCustomers?${qs}`, {
        headers: getAuthHeaders(),
      });
      const pagination = response.headers.pagination ? JSON.parse(response.headers.pagination) : {};
      const rows = getResponseRows(response.data);
      setDrivers(rows);
      setTotalItems(pagination.totalItems || 0);
      setTotalPages(pagination.totalPages || 1);
      setPageNumber(pagination.currentPage || pageNumber);
      if (!Array.isArray(response.data) && rows.length === 0 && response.data) {
        setNotice("Unable to load drivers. Please confirm the backend Drivers API DLL has been deployed.");
      }
    } catch (e) {
      if (e.response && e.response.status === 401) {
        props.history.push("/login");
      } else if (e.response && e.response.status === 403) {
        props.history.push("/unauthorise");
      } else {
        setNotice("Unable to load drivers.");
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingDriver(null);
    setFormData({
      ...blankDriver,
      customerId: selectedCustomerId || "",
    });
    setErrors([]);
    setModalOpen(true);
  };

  const openSampleCreate = () => {
    setEditingDriver(null);
    setFormData(buildSampleDriver(selectedCustomerId));
    setErrors([]);
    setModalOpen(true);
  };

  const openEdit = (driver) => {
    setEditingDriver(driver);
    setFormData(buildFormState(driver));
    setErrors([]);
    setModalOpen(true);
  };

  const handleFormChange = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const saveDriver = async () => {
    setSaving(true);
    setErrors([]);
    setNotice("");
    try {
      const payload = toApiPayload(formData);
      if (editingDriver) {
        await axios.put(`${apiHost}/api/Customer/UpdateDriver/${editingDriver.id}`, payload, {
          headers: getAuthHeaders(),
        });
      } else {
        await axios.post(`${apiHost}/api/Customer/CreateDriver`, payload, {
          headers: getAuthHeaders(),
        });
      }
      setModalOpen(false);
      await loadDrivers();
      setNotice(editingDriver ? "Driver updated." : "Driver created.");
    } catch (e) {
      if (e.response && e.response.data && e.response.data.errors) {
        setErrors(e.response.data.errors);
      } else if (e.response && e.response.data && e.response.data.message) {
        setErrors([e.response.data.message]);
      } else {
        setErrors(["Unable to save driver."]);
      }
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (driver) => {
    setDeleteDriverTarget(driver);
    setDeleteModalOpen(true);
  };

  const confirmDeleteDriver = async () => {
    if (!deleteDriverTarget) {
      return;
    }

    setDeleteSaving(true);
    setNotice("");
    try {
      await axios.delete(`${apiHost}/api/Customer/DeleteDriver/${deleteDriverTarget.id}`, {
        headers: getAuthHeaders(),
      });
      setModalOpen(false);
      setDeleteModalOpen(false);
      await loadDrivers();
      setNotice("Driver deleted.");
    } catch (e) {
      setDeleteModalOpen(false);
      setNotice("Unable to delete driver. The driver may have existing log history.");
    } finally {
      setDeleteSaving(false);
      setDeleteDriverTarget(null);
    }
  };

  const regeneratePin = async (driver) => {
    setPinDriver(driver);
    setNewPin("");
    setPinModalOpen(true);
  };

  const confirmRegeneratePin = async () => {
    if (!pinDriver) {
      return;
    }

    setPinSaving(true);
    setNotice("");
    try {
      const response = await axios.post(`${apiHost}/api/Customer/RegenerateDriverPin/${pinDriver.id}`, {}, {
        headers: getAuthHeaders(),
      });
      await loadDrivers();
      setNewPin(response.data.newPin || "");
    } catch (e) {
      setPinModalOpen(false);
      setNotice("Unable to regenerate driver PIN.");
    } finally {
      setPinSaving(false);
    }
  };

  const closePinModal = () => {
    setPinModalOpen(false);
    if (newPin && pinDriver) {
      setNotice(`New PIN generated for ${pinDriver.fullName}.`);
    }
    setPinDriver(null);
    setNewPin("");
  };

  const entryStart = totalItems === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const entryEnd = Math.min(pageNumber * pageSize, totalItems);
  const formLicenceExpired = isExpired(formData.licenceExpiry);

  return (
    <Layout>
      <Helmet>
        <title>Drivers</title>
      </Helmet>
      <div className="contentBody">
        <div className="contentWrapper driver-page">
          <div className="driver-page-header">
            <div>
              <h2>Drivers</h2>
              <p>Manage driver records, licence details, emergency contacts and QR login PINs.</p>
              {selectedCustomerId && filteredCustomerName && (
                <button className="driver-filter-pill" onClick={() => updateCustomerFilter("", "")} type="button" title="Remove customer filter">
                  <i className="fa fa-times"></i>
                  {filteredCustomerName}
                </button>
              )}
            </div>
            <div className="driver-header-actions">
              <RoleAccessBadge role="CommunityManager" />
              <Button className="driver-sample-button" onClick={openSampleCreate}>
                <i className="fa fa-magic"></i>
                Sample driver
              </Button>
              <Button className="driver-create-button" onClick={openCreate}>
                <i className="fa fa-plus"></i>
                Add driver
              </Button>
            </div>
          </div>

          {notice && <Alert variant="info" className="driver-alert">{notice}</Alert>}

          <div className="driver-toolbar">
            <div className="driver-search">
              <i className="fa fa-search"></i>
              <FormControl
                aria-label="Search drivers"
                onChange={(e) => {
                  setPageNumber(1);
                  setSearchText(e.target.value);
                }}
                placeholder="Search name, licence, customer, mobile or email"
                value={searchText}
              />
              {searchText && (
                <button onClick={() => setSearchText("")} type="button">
                  <i className="fa fa-times"></i>
                </button>
              )}
            </div>

            {isSystemAdmin && (
              <Form.Control
                as="select"
                className="driver-customer-filter"
                value={selectedCustomerId}
                onChange={(e) => {
                  updateCustomerFilter(e.target.value);
                }}
              >
                <option value="">All active customers</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>{customer.name}</option>
                ))}
              </Form.Control>
            )}
          </div>

          <div className="driver-table-shell">
            <Table responsive hover className="driver-table">
              <thead>
                <tr>
                  {isSystemAdmin && <th>Customer</th>}
                  <th>Name</th>
                  <th>Licence</th>
                  <th>Class</th>
                  <th>Expiry</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>PIN</th>
                  <th className="driver-actions-column">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={isSystemAdmin ? 9 : 8} className="driver-empty-cell">
                      <Spinner animation="border" size="sm" /> Loading drivers
                    </td>
                  </tr>
                ) : drivers.length === 0 ? (
                  <tr>
                    <td colSpan={isSystemAdmin ? 9 : 8} className="driver-empty-cell">No drivers found</td>
                  </tr>
                ) : (
                  drivers.map((driver) => {
                    const licenceExpired = isExpired(driver.licenceExpiry);

                    return (
                      <tr key={driver.id} className={licenceExpired ? "driver-expired-row" : ""}>
                        {isSystemAdmin && (
                          <td>
                            {driver.customerId && driver.customerName ? (
                              <button
                                className="driver-customer-link"
                                onClick={() => updateCustomerFilter(driver.customerId, driver.customerName)}
                                title={`Filter drivers by ${driver.customerName}`}
                                type="button"
                              >
                                {driver.customerName}
                              </button>
                            ) : "-"}
                          </td>
                        )}
                        <td className="driver-name-cell">{driver.fullName}</td>
                        <td>{driver.licenceNumber}</td>
                        <td><span className="driver-class-pill">{driver.licenceClass}</span></td>
                        <td className={licenceExpired ? "driver-expired" : ""}>
                          {formatDate(driver.licenceExpiry)}
                        </td>
                        <td>{driver.mobile || "-"}</td>
                        <td>{driver.email || "-"}</td>
                        <td><span className="driver-pin-pill">{driver.pinCode || "-"}</span></td>
                        <td className="driver-actions-cell">
                          <button type="button" className="driver-row-action edit" onClick={() => openEdit(driver)} title="Edit driver">
                            <i className="fa fa-pencil"></i>
                            <span>Edit</span>
                          </button>
                          <button type="button" className="driver-row-action pin" onClick={() => regeneratePin(driver)} title="Regenerate PIN">
                            <i className="fa fa-key"></i>
                            <span>PIN</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <div className="driver-pagination">
            <div>
              Showing <strong>{entryStart}</strong> to <strong>{entryEnd}</strong> of <strong>{totalItems}</strong> drivers
            </div>
            <div className="driver-pagination-actions">
              <label>
                Rows
                <select value={pageSize} onChange={(e) => {
                  setPageNumber(1);
                  setPageSize(Number(e.target.value));
                }}>
                  {[50, 100, 200].map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </label>
              <button disabled={pageNumber <= 1} onClick={() => setPageNumber(pageNumber - 1)} type="button">Previous</button>
              <span>Page <strong>{pageNumber}</strong> of <strong>{totalPages}</strong></span>
              <button disabled={pageNumber >= totalPages} onClick={() => setPageNumber(pageNumber + 1)} type="button">Next</button>
            </div>
          </div>
        </div>
      </div>

      <Modal show={modalOpen} onHide={() => setModalOpen(false)} size="lg" centered className="driver-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingDriver ? "Edit Driver" : "Add Driver"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errors.length > 0 && (
            <Alert variant="danger">
              {errors.map((error) => <div key={error}>{error}</div>)}
            </Alert>
          )}

          <Form>
            {isSystemAdmin && (
              <div className="driver-form-section">
                <h3>Customer</h3>
                <Form.Group>
                  <Form.Label>Customer *</Form.Label>
                  <Form.Control as="select" value={formData.customerId} onChange={(e) => handleFormChange("customerId", e.target.value)}>
                    <option value="">Select customer</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </div>
            )}

            <div className="driver-form-section">
              <h3>Driver Details</h3>
              <div className="driver-form-grid">
                <Form.Group>
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control value={formData.fullName} onChange={(e) => handleFormChange("fullName", e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Date of Birth *</Form.Label>
                  <Form.Control type="date" value={formData.dateOfBirth} onChange={(e) => handleFormChange("dateOfBirth", e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Mobile</Form.Label>
                  <Form.Control value={formData.mobile} onChange={(e) => handleFormChange("mobile", e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control value={formData.email} onChange={(e) => handleFormChange("email", e.target.value)} />
                </Form.Group>
              </div>
            </div>

            <div className="driver-form-section">
              <h3>Address</h3>
              <div className="driver-form-grid">
                <Form.Group>
                  <Form.Label>Address Line 1 *</Form.Label>
                  <Form.Control value={formData.addressLine1} onChange={(e) => handleFormChange("addressLine1", e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Address Line 2</Form.Label>
                  <Form.Control value={formData.addressLine2} onChange={(e) => handleFormChange("addressLine2", e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Suburb *</Form.Label>
                  <Form.Control value={formData.suburb} onChange={(e) => handleFormChange("suburb", e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>State *</Form.Label>
                  <Form.Control as="select" value={formData.state} onChange={(e) => handleFormChange("state", e.target.value)}>
                    {states.map((state) => <option key={state} value={state}>{state}</option>)}
                  </Form.Control>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Postcode *</Form.Label>
                  <Form.Control value={formData.postcode} onChange={(e) => handleFormChange("postcode", e.target.value)} />
                </Form.Group>
              </div>
            </div>

            <div className="driver-form-section">
              <h3>Emergency Contact</h3>
              <div className="driver-form-grid">
                <Form.Group>
                  <Form.Label>Contact Name *</Form.Label>
                  <Form.Control value={formData.emergencyName} onChange={(e) => handleFormChange("emergencyName", e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Phone *</Form.Label>
                  <Form.Control value={formData.emergencyPhone} onChange={(e) => handleFormChange("emergencyPhone", e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Relationship</Form.Label>
                  <Form.Control value={formData.emergencyRelation} onChange={(e) => handleFormChange("emergencyRelation", e.target.value)} />
                </Form.Group>
              </div>
            </div>

            <div className={`driver-form-section driver-licence-section ${formLicenceExpired ? "expired" : ""}`}>
              <div className="driver-section-heading">
                <h3>Licence Details</h3>
                {formLicenceExpired && (
                  <span className="driver-expired-badge">
                    <i className="fa fa-exclamation-triangle"></i>
                    Expired licence
                  </span>
                )}
              </div>
              <div className="driver-form-grid">
                <Form.Group>
                  <Form.Label>Licence Number *</Form.Label>
                  <Form.Control value={formData.licenceNumber} onChange={(e) => handleFormChange("licenceNumber", e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Licence Class *</Form.Label>
                  <Form.Control as="select" value={formData.licenceClass} onChange={(e) => handleFormChange("licenceClass", e.target.value)}>
                    {licenceClasses.map((licenceClass) => <option key={licenceClass} value={licenceClass}>{licenceClass}</option>)}
                  </Form.Control>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Licence Expiry *</Form.Label>
                  <Form.Control className={formLicenceExpired ? "driver-expired-input" : ""} type="date" value={formData.licenceExpiry} onChange={(e) => handleFormChange("licenceExpiry", e.target.value)} />
                  {formLicenceExpired && (
                    <div className="driver-expired-helper">
                      Licence expired on {formatDate(formData.licenceExpiry)}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="driver-form-wide">
                  <Form.Label>Endorsements</Form.Label>
                  <Form.Control value={formData.endorsements} onChange={(e) => handleFormChange("endorsements", e.target.value)} placeholder="e.g. Forklift, DG, Passenger" />
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {editingDriver && (
            <Button variant="outline-danger" className="mr-auto driver-delete-button" onClick={() => openDeleteModal(editingDriver)}>
              <i className="fa fa-trash"></i>
              Delete driver
            </Button>
          )}
          <Button variant="light" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={saveDriver}>
            {saving ? "Saving..." : "Save Driver"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={pinModalOpen} onHide={closePinModal} centered className="driver-pin-modal">
        <Modal.Header closeButton>
          <Modal.Title>Regenerate Driver PIN</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="driver-pin-warning">
            <div className="driver-pin-warning-icon">
              <i className="fa fa-key"></i>
            </div>
            <div>
              <h3>{pinDriver ? pinDriver.fullName : "Driver"}</h3>
              <p>
                Regenerating this PIN will disable the previous PIN immediately. The driver will need the new PIN before
                they can use QR driver login again.
              </p>
            </div>
          </div>

          {newPin ? (
            <div className="driver-new-pin-panel">
              <span>New PIN</span>
              <strong>{newPin}</strong>
              <p>Pass this PIN to the driver before closing this window.</p>
            </div>
          ) : (
            <div className="driver-pin-checklist">
              <div><i className="fa fa-check-circle"></i> Previous PIN will stop working.</div>
              <div><i className="fa fa-check-circle"></i> Driver must be told the new PIN.</div>
              <div><i className="fa fa-check-circle"></i> This action is recorded against the driver record.</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={closePinModal}>{newPin ? "Done" : "Cancel"}</Button>
          {!newPin && (
            <Button variant="primary" disabled={pinSaving} onClick={confirmRegeneratePin}>
              {pinSaving ? "Generating..." : "Generate new PIN"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={deleteModalOpen} onHide={() => setDeleteModalOpen(false)} centered className="driver-delete-modal">
        <Modal.Header closeButton>
          <Modal.Title>Delete Driver</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="driver-delete-warning">
            <div className="driver-delete-warning-icon">
              <i className="fa fa-trash"></i>
            </div>
            <div>
              <h3>{deleteDriverTarget ? deleteDriverTarget.fullName : "Driver"}</h3>
              <p>
                This will permanently remove the driver record. If this driver has existing logs, checklist runs,
                or driver login history, the database may prevent the delete so historical records stay intact.
              </p>
            </div>
          </div>

          <div className="driver-delete-details">
            <div>
              <span>Customer</span>
              <strong>{deleteDriverTarget && deleteDriverTarget.customerName ? deleteDriverTarget.customerName : "-"}</strong>
            </div>
            <div>
              <span>Licence</span>
              <strong>{deleteDriverTarget && deleteDriverTarget.licenceNumber ? deleteDriverTarget.licenceNumber : "-"}</strong>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button variant="danger" disabled={deleteSaving} onClick={confirmDeleteDriver}>
            {deleteSaving ? "Deleting..." : "Delete driver"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}

export default Drivers;
